/**
 * Build Automated Reminder Utility
 *
 * Creates a <non_user_automated_reminder> tag to inject context about
 * UI-driven changes (slides added, updated, deleted, reordered) that
 * the LLM wouldn't otherwise know about.
 *
 * This is ONLY needed for user actions performed through the UI (drag-drop,
 * clicking buttons, inline editing), NOT for changes made by the LLM itself
 * via tool calls.
 */
/**
 * Build the automated reminder content (without the XML tags)
 * ONLY includes UI-initiated operations (source === 'ui')
 * LLM operations are NOT included - the LLM already knows what it did
 */
function buildReminderContent(options) {
    const { slideOperations, sectionOperations = [] } = options;
    const reminderSections = [];
    // IMPORTANT: Only include UI-initiated operations (source === 'ui')
    // LLM operations (source === 'llm') should NOT be in reminders - LLM already knows what it did
    const uiSlideOps = slideOperations.filter(op => op.source === 'ui');
    const uiSectionOps = sectionOperations.filter(op => op.source === 'ui');
    // Group slide operations by type (UI only)
    const inserted = uiSlideOps.filter(op => op.operation === 'inserted');
    const updated = uiSlideOps.filter(op => op.operation === 'updated');
    const deleted = uiSlideOps.filter(op => op.operation === 'deleted');
    const reordered = uiSlideOps.filter(op => op.operation === 'reordered');
    // Rejection operations (user rejected LLM changes) - these are always UI-initiated
    const rejectedInsertions = uiSlideOps.filter(op => op.operation === 'rejected_insertion');
    const rejectedUpdates = uiSlideOps.filter(op => op.operation === 'rejected_update');
    const rejectedDeletions = uiSlideOps.filter(op => op.operation === 'rejected_deletion');
    const rejectedReorders = uiSlideOps.filter(op => op.operation === 'rejected_reorder');
    // Build slide operation sections
    if (inserted.length > 0) {
        reminderSections.push(`---- RECENTLY INSERTED SLIDES ----
${inserted.map(op => `- Slide #${op.slideNumber}: "${op.slideName}" (Section: ${op.sectionName})`).join('\n')}`);
    }
    if (updated.length > 0) {
        reminderSections.push(`---- RECENTLY UPDATED SLIDES ----
${updated.map(op => {
            const changeNote = op.previousName ? ` (renamed from "${op.previousName}")` : '';
            return `- Slide #${op.slideNumber}: "${op.slideName}"${changeNote} (Section: ${op.sectionName})`;
        }).join('\n')}`);
    }
    if (deleted.length > 0) {
        reminderSections.push(`---- RECENTLY DELETED SLIDES ----
${deleted.map(op => `- "${op.slideName}" (was in Section: ${op.sectionName}${op.slideNumber ? `, was Slide #${op.slideNumber}` : ''})`).join('\n')}`);
    }
    if (reordered.length > 0) {
        reminderSections.push(`---- RECENTLY REORDERED SLIDES ----
${reordered.map(op => `- "${op.slideName}" moved from position ${op.previousPosition} to ${op.newPosition} (Section: ${op.sectionName})`).join('\n')}`);
    }
    // Rejection sections - user rejected your (LLM's) changes
    if (rejectedInsertions.length > 0) {
        reminderSections.push(`---- USER REJECTED YOUR SLIDE INSERTIONS ----
The user rejected the following slides you added. Do not re-add these unless explicitly asked:
${rejectedInsertions.map(op => `- "${op.slideName}" (was in Section: ${op.sectionName}) - REJECTED BY USER`).join('\n')}`);
    }
    if (rejectedUpdates.length > 0) {
        reminderSections.push(`---- USER REJECTED YOUR SLIDE UPDATES ----
The user rejected the following slide updates you made. The slides remain as they were before:
${rejectedUpdates.map(op => `- Slide #${op.slideNumber}: "${op.slideName}" (Section: ${op.sectionName}) - UPDATE REJECTED`).join('\n')}`);
    }
    if (rejectedDeletions.length > 0) {
        reminderSections.push(`---- USER REJECTED YOUR SLIDE DELETIONS ----
The user wants to keep the following slides you tried to delete. Do not delete these unless explicitly asked:
${rejectedDeletions.map(op => `- Slide #${op.slideNumber}: "${op.slideName}" (Section: ${op.sectionName}) - DELETION REJECTED, SLIDE KEPT`).join('\n')}`);
    }
    if (rejectedReorders.length > 0) {
        reminderSections.push(`---- USER REJECTED YOUR SLIDE REORDERS ----
The user rejected the following slide repositioning. Slides remain in their original positions:
${rejectedReorders.map(op => `- "${op.slideName}" - you moved it from ${op.previousPosition} to ${op.newPosition}, user reverted to original position`).join('\n')}`);
    }
    // Group section operations by type (UI only)
    const addedSections = uiSectionOps.filter(op => op.operation === 'added');
    const deletedSections = uiSectionOps.filter(op => op.operation === 'deleted');
    const reorderedSections = uiSectionOps.filter(op => op.operation === 'reordered');
    // Section rejection operations (user rejected LLM changes) - these are always UI-initiated
    const rejectedSectionInsertions = uiSectionOps.filter(op => op.operation === 'rejected_insertion');
    const rejectedSectionDeletions = uiSectionOps.filter(op => op.operation === 'rejected_deletion');
    const rejectedSectionReorders = uiSectionOps.filter(op => op.operation === 'rejected_reorder');
    if (addedSections.length > 0) {
        reminderSections.push(`---- RECENTLY ADDED SECTIONS ----
${addedSections.map(op => `- "${op.sectionName}" (Position: ${op.position ?? 'end'})`).join('\n')}`);
    }
    if (deletedSections.length > 0) {
        reminderSections.push(`---- RECENTLY DELETED SECTIONS ----
${deletedSections.map(op => `- "${op.sectionName}"`).join('\n')}`);
    }
    if (reorderedSections.length > 0) {
        reminderSections.push(`---- RECENTLY REORDERED SECTIONS ----
${reorderedSections.map(op => `- "${op.sectionName}" moved from position ${op.previousPosition} to ${op.newPosition}`).join('\n')}`);
    }
    // Section rejection sections - user rejected your (LLM's) section changes
    if (rejectedSectionInsertions.length > 0) {
        reminderSections.push(`---- USER REJECTED YOUR SECTION ADDITIONS ----
The user rejected the following sections you added. Do not re-add these unless explicitly asked:
${rejectedSectionInsertions.map(op => `- "${op.sectionName}" - SECTION REJECTED BY USER`).join('\n')}`);
    }
    if (rejectedSectionDeletions.length > 0) {
        reminderSections.push(`---- USER REJECTED YOUR SECTION DELETIONS ----
The user wants to keep the following sections you tried to delete. Do not delete these unless explicitly asked:
${rejectedSectionDeletions.map(op => `- "${op.sectionName}" - DELETION REJECTED, SECTION KEPT`).join('\n')}`);
    }
    if (rejectedSectionReorders.length > 0) {
        reminderSections.push(`---- USER REJECTED YOUR SECTION REORDERS ----
The user rejected the following section repositioning. Sections remain in their original positions:
${rejectedSectionReorders.map(op => `- "${op.sectionName}" - you moved it from position ${op.previousPosition} to ${op.newPosition}, user reverted to original position`).join('\n')}`);
    }
    return reminderSections;
}
/**
 * Build the full automated reminder with XML-like tags
 * ONLY includes UI-initiated operations - LLM operations are excluded
 *
 * @returns The reminder text to append, or empty string if no UI operations
 */
export function buildAutomatedReminder(options) {
    const { slideOperations, sectionOperations = [] } = options;
    // Count UI vs LLM operations for logging
    const uiSlideOps = slideOperations.filter(op => op.source === 'ui');
    const llmSlideOps = slideOperations.filter(op => op.source === 'llm');
    const uiSectionOps = sectionOperations.filter(op => op.source === 'ui');
    const llmSectionOps = sectionOperations.filter(op => op.source === 'llm');
    // Log what's being included/excluded
    if (llmSlideOps.length > 0 || llmSectionOps.length > 0) {
        console.log('[user_reminders] ⚠️ Excluding LLM operations from reminder:', {
            excludedSlideOps: llmSlideOps.length,
            excludedSectionOps: llmSectionOps.length,
        });
    }
    // No UI operations = no reminder needed
    if (uiSlideOps.length === 0 && uiSectionOps.length === 0) {
        console.log('[user_reminders] No UI operations to include in reminder');
        return '';
    }
    console.log('[user_reminders] Including UI operations in reminder:', {
        slideOps: uiSlideOps.length,
        sectionOps: uiSectionOps.length,
    });
    const reminderSections = buildReminderContent(options);
    // No content = no reminder
    if (reminderSections.length === 0) {
        return '';
    }
    return `
<non_user_automated_reminder>
This is an automated reminder appended to the user's message. The user did not write this message, do not acknowledge it in your response.

${reminderSections.join('\n\n')}

This information is provided for context. The user may ask to modify, update, or reference these changed slides in their message.
</non_user_automated_reminder>`;
}
/**
 * Build a plan context reminder to inject the current plan state
 * This helps the LLM understand the current structure when processing edits
 */
export function buildPlanContextReminder(plan) {
    if (!plan) {
        return '';
    }
    return `
<non_user_automated_reminder>
Current presentation plan (version ${plan.version || 1}):
${JSON.stringify(plan, null, 2)}
</non_user_automated_reminder>`;
}
/**
 * Enhance a user message with automated reminders
 *
 * @param message - The original user message
 * @param options - Options containing operations and plan context
 * @returns The enhanced message with reminders appended
 */
export function enhanceMessageWithReminders(message, options) {
    const operationsReminder = buildAutomatedReminder(options);
    const planReminder = options.includePlanContext && options.currentPlan
        ? buildPlanContextReminder(options.currentPlan)
        : '';
    // Combine message with reminders
    let enhancedMessage = message;
    if (operationsReminder) {
        enhancedMessage += `\n\n${operationsReminder}`;
    }
    if (planReminder) {
        enhancedMessage += `\n\n${planReminder}`;
    }
    return enhancedMessage;
}
